// Error reports (owner 2026-10-25: players write "the game does not work" and we learn of it last). The game sends
// what went wrong to POST /errors (server/routes/errors.js); the owner reads it in /admin, tab Errors:
// - `js` / `promise`: an uncaught error or rejected promise whose stack is in our own files (public_html/ui/);
//   the ad and analytics scripts throw all the time and are not ours, so they are left out;
// - `boot`: our script or a part of it did not load (a deploy while the page was open, a broken connection);
// - `react`: the view crashed (app-entry.tsx, createRoot's onUncaughtError);
// - `hang`: the board stayed in a deal or an animation (app-entry.tsx);
// - `asset`: a picture of ours did not load;
// - `player`: the report a player attaches to a contact message (windows/SimpleWindows.tsx InfoWindow).
// Each report carries the state of the game and the device (setReportContext), so the owner can deal the same
// cards again (the shuffle pattern). At most a few automatic reports per page load, each error once.
// Installed by main.tsx before the app loads; the page template's inline catcher (server/views/_base.html) keeps
// the errors of the first moments in window.__earlyErrors until then.
import { env } from '../env';

export type ReportKind = 'js' | 'promise' | 'react' | 'boot' | 'hang' | 'asset' | 'player';

interface EarlyError { message?: string; filename?: string; stack?: string; src?: string }
declare global {
  interface Window { __earlyErrors?: EarlyError[] | undefined; __solitaireStarted?: boolean }
}

/** Automatic reports one page load may send (a loop of errors must not flood the server). */
const MAX_AUTO = 8;
/** Our own scripts: the app and its chunks (frontend/vite.config.ts builds them into public_html/ui/). */
const OWN_SCRIPT = /\/ui\/(?:app\.js|chunks\/)/;

let contextOf: () => Record<string, unknown> = () => ({});
let autoSent = 0;
const seen = new Set<string>();
/** The errors of this page load, sent or not: a player's report lists them. */
const recent: { s: number; kind: ReportKind; message: string }[] = [];
/** The last lines the app wrote with console.error (React writes its warnings there). */
const consoleLines: string[] = [];

/** What the reports say about the game and the device; app-entry.tsx sets it once the game exists. */
export function setReportContext(fn: () => Record<string, unknown>): void {
  contextOf = fn;
}

/** In the e2e tests only when a test turns them on (a report is a request some tests would count). */
const enabled = (): boolean =>
  !window.__E2E__ || (() => { try { return localStorage.getItem('solitaire_storage:reports-e2e') === 'on'; } catch { return false; } })();

const own = (s: string | undefined | null): boolean => !!s && OWN_SCRIPT.test(s);
/** The script an error was thrown in: the first address in its stack after the message (Chrome "at f (url:1:2)",
 *  Firefox and Safari "f@url:1:2"; a "[native code]" frame has none), else the event's file. Not any frame of ours
 *  (2026-11-02: the consent window's own script failed inside our Privacy click, Safari 26 / iOS 18, "af is not a
 *  function" in cmp2.js, and was reported as ours because our onClick was further down the stack). Any address
 *  counts, not only http(s): 2026-11-04, a Chrome extension's fetch wrapper (`chrome-extension://…/hook.js`) rejected
 *  its own copy of our fetch with "Failed to fetch" (our call caught it) and was reported as ours, its frame skipped. */
const thrownIn = (stack: string | undefined, message: string | undefined, filename?: string): string | undefined => {
  const frames = stack && message && stack.includes(message) ? stack.slice(stack.indexOf(message) + message.length) : stack;
  return frames?.match(/[a-z][a-z0-9+.-]*:\/\/[^\s)]+/i)?.[0] ?? filename;
};
/** Firefox does not let a page script read some nodes (the browser's own parts: a scrollbar, a form control's inside,
 *  the window's edge) and throws when React's mouseout / pointerout handling reads its key on such a `relatedTarget`
 *  (2026-10-31, Firefox 159, react-dom getClosestInstanceFromNode, no frame of ours): one enter / leave event is lost,
 *  nothing else, and we cannot change it. */
const FIREFOX_DENIED = /^(?:\w*Error: )?Permission denied to access property "__react/;
const ownImage = (src: string): boolean => {
  try {
    const u = new URL(src, location.href);
    return u.origin === new URL(env.ss, location.href).origin && u.pathname.includes('/images/');
  } catch {
    return false;
  }
};

function context(): Record<string, unknown> {
  try {
    return contextOf();
  } catch (err) {
    return { contextError: String(err) };
  }
}

function payload(kind: ReportKind, message: string, stack?: string, extra?: Record<string, unknown>) {
  const ctx = context();
  return {
    kind,
    message: message.slice(0, 1000),
    stack: stack?.slice(0, 8000),
    url: location.href,
    game: typeof ctx.game === 'string' ? ctx.game : undefined,
    revision: env.revision.replace(/^\?v=/, ''),
    context: { ...ctx, ...extra },
  };
}

/** sendBeacon survives the page closing but takes at most 64 KB; a bigger body (a player's picture) goes by fetch. */
async function post(body: unknown): Promise<boolean> {
  const json = JSON.stringify(body);
  const url = `${env.fullAddr}errors`;
  try {
    if (json.length < 60_000 && navigator.sendBeacon?.(url, new Blob([json], { type: 'application/json' }))) return true;
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, credentials: 'include', keepalive: json.length < 60_000 });
    return res.ok;
  } catch {
    return false;
  }
}

/** Sends an automatic report, once per kind and message in a page load. */
export function reportError(kind: ReportKind, message: string, stack?: string, extra?: Record<string, unknown>): void {
  recent.push({ s: Math.round(performance.now() / 1000), kind, message: message.slice(0, 300) });
  if (recent.length > 10) recent.shift();
  // a picture: one report a page, however many fail (no connection fails them all)
  const key = kind === 'asset' ? 'asset' : `${kind}|${message}`;
  if (seen.has(key) || autoSent >= MAX_AUTO || !enabled()) return;
  seen.add(key);
  autoSent += 1;
  // a hang carries this page's earlier errors and console lines, like a player's report: an error thrown in the middle of
  // a deal is the usual cause of a board left "dealing" (2026-10-29); a JavaScript error carries the earlier errors too
  // (owner 2026-10-31: React's "should not already be working" is the follow-up of whatever broke first)
  const more =
    kind === 'hang'
      ? { errors: recent.slice(0, -1), console: consoleLines.slice(), ...extra }
      : kind === 'js' && recent.length > 1
        ? { errors: recent.slice(0, -1), ...extra }
        : extra;
  void post(payload(kind, message, stack, more));
}

/** A player's report with a contact message: the picture of the board, the state, this page's errors. */
export function sendPlayerReport(ref: string, message: string, image: string | null): Promise<boolean> {
  return post({ ...payload('player', message.slice(0, 300), undefined, { errors: recent.slice(), console: consoleLines.slice() }), ref, image: image ?? undefined });
}

function fromErrorEvent(e: { message?: string; filename?: string; stack?: string; error?: unknown }) {
  const stack = (e.error as { stack?: unknown } | undefined)?.stack;
  const st = typeof stack === 'string' ? stack : e.stack;
  const text = e.error instanceof Error ? e.error.message : undefined;
  if (!own(thrownIn(st, text, e.filename)) || FIREFOX_DENIED.test(text ?? e.message ?? '')) return;
  reportError('js', e.message || 'Error', st ?? `${e.filename ?? ''}`);
}

function fromLoadFailure(target: EventTarget | null) {
  if (target instanceof HTMLScriptElement && own(target.src)) reportError('boot', `script did not load: ${target.src}`);
  else if (target instanceof HTMLLinkElement && /\/ui\/app\.css/.test(target.href)) reportError('boot', `stylesheet did not load: ${target.href}`);
  // the page template's hidden "browser not supported" box (#no_support_box, its browser logos): never seen, not worth
  // a report (2026-10-25: firefox.png failed now and then; the pictures are lazy there, a browser without lazy loading
  // still fetches them)
  else if (target instanceof HTMLImageElement && navigator.onLine !== false && !target.closest('#no_support_box')) {
    const src = target.currentSrc || target.src;
    if (ownImage(src)) reportError('asset', `picture did not load: ${new URL(src, location.href).pathname}`);
  }
}

/** A rejected import() of one of our chunks: the page is older than the files on the server, or offline. */
const IMPORT_FAILED = /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function fromRejection(reason: unknown) {
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;
  if (IMPORT_FAILED.test(message)) reportError('boot', message, stack);
  else if (own(thrownIn(stack, message))) reportError('promise', message, stack);
}

export function installErrorReports(): void {
  window.addEventListener(
    'error',
    (e) => {
      if (e instanceof ErrorEvent) fromErrorEvent(e);
      else fromLoadFailure((e as Event).target);
    },
    true, // a picture's or a script's failed load does not bubble
  );
  window.addEventListener('unhandledrejection', (e) => fromRejection(e.reason));
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    consoleLines.push(args.map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : (() => { try { return JSON.stringify(a); } catch { return String(a); } })())).join(' ').slice(0, 300));
    if (consoleLines.length > 10) consoleLines.shift();
    original(...args);
  };
  for (const e of window.__earlyErrors ?? []) {
    if (e.src) fromLoadFailure(Object.assign(document.createElement('script'), { src: e.src }));
    else fromErrorEvent(e);
  }
  window.__earlyErrors = undefined;
}

/** A short id that ties a player's report to the contact message (crypto: Math.random is the deal's, the
 *  e2e fixtures seed it). */
export function newReportRef(): string {
  const b = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(b, (x) => (x % 36).toString(36)).join('') + Date.now().toString(36).slice(-4);
}
